/**
 * Spec 019 US4 (T033/T034) — cascade-round extraction.
 *
 * Turns one `ReplayedOutcome`'s `state_path` into an ordered sequence
 * of **cascade rounds**, one per `evaluate` step, for the
 * `CascadePanel` tumble view.
 *
 * A cascade is just "more evaluates per spin" (spec 019 reuses the
 * multi-evaluate path — CLAUDE.md §13.4): each round's grid +
 * multiplier ride the `evaluate` step's `detail.grid` / `detail.win` /
 * `detail.multiplier` (spec 006.3), and the two new cascade ops append
 * their own steps:
 *
 * - **`remove_winning_symbols`** → `detail.removed` is the set of grid
 *   positions the immediately-preceding `evaluate`'s winning mask
 *   cleared. In the loop body (remove → drop → evaluate → increment),
 *   this op runs at the START of the next iteration, so the cells it
 *   names are the *most-recent* round's winners — they vacate to
 *   produce the next round's grid.
 * - **`drop_symbols`** → refills the vacated cells (HWM). The refilled
 *   symbols surface as the next round's `evaluate` grid; this module
 *   derives the per-round `dropped` cells by diffing consecutive round
 *   grids, so the "what dropped in" highlight needs no gravity model.
 *
 * The final round (the chain's terminating no-win evaluate, or the
 * grid clipped at `max_cascade_depth`) has no following
 * `remove_winning_symbols`, so its `removed` is empty — it does not
 * vacate. A single-evaluation (non-cascading) spin yields exactly one
 * round with empty `removed`/`dropped` — the graceful degenerate case
 * (US4 acceptance scenario 3).
 */

import type { ReplayedOutcome } from "./types";

export interface CascadeRound {
  /** 0-based round index within the spin (round 0 is the initial spin). */
  index: number;
  /** State the round's `evaluate` ran in (`base_game` then `cascade`). */
  state: string;
  /** Reel-major grid (`grid[reel][row]`) as evaluated this round. */
  grid: string[][];
  /** Accumulating per-round multiplier applied to this round's win. */
  multiplier: number;
  /** Win paid this round, in stake multiples. */
  win: number;
  /**
   * Winning cells removed after this round (the following
   * `remove_winning_symbols`'s mask). Empty on the terminating round.
   */
  removed: Array<[number, number]>;
  /**
   * Cells whose symbol changed versus the previous round — the
   * refill that dropped in. Empty on round 0 (the initial spin).
   */
  dropped: Array<[number, number]>;
}

/**
 * Extract the ordered cascade rounds from a replayed outcome. One round
 * per `evaluate` step; `removed` attaches to the round whose winners a
 * later `remove_winning_symbols` clears, and `dropped` is derived by
 * diffing consecutive round grids.
 */
export function extractCascadeRounds(outcome: ReplayedOutcome): CascadeRound[] {
  const rounds: CascadeRound[] = [];
  let current: CascadeRound | null = null;
  let lastGrid: string[][] = outcome.grid;

  for (const step of outcome.state_path) {
    const detail = step.detail ?? {};
    if (step.action_kind === "evaluate") {
      const grid = asGrid(detail["grid"]) ?? lastGrid;
      lastGrid = grid;
      current = {
        index: rounds.length,
        state: step.state,
        grid,
        multiplier: asNumber(detail["multiplier"]) ?? 1,
        win: asNumber(detail["win"]) ?? 0,
        removed: [],
        dropped: [],
      };
      rounds.push(current);
    } else if (
      step.action_kind === "remove_winning_symbols" &&
      current != null
    ) {
      // This op clears the most-recent evaluate's winners (loop body is
      // remove → drop → evaluate → increment, so the remove at the top
      // of iteration k+1 names round k's winners).
      current.removed = asPositions(detail["removed"]);
    }
  }

  // Derive `dropped` per round by diffing against the previous round's
  // grid (the refilled cells are exactly those whose symbol changed).
  for (let i = 1; i < rounds.length; i++) {
    const prev = rounds[i - 1];
    const round = rounds[i];
    if (prev === undefined || round === undefined) continue;
    round.dropped = diffCells(prev.grid, round.grid);
  }

  // Degenerate `state_path` (e.g. a GPU-synthesised minimal path with no
  // evaluate step): still surface the end-of-spin grid as one round.
  if (rounds.length === 0) {
    rounds.push({
      index: 0,
      state: outcome.state_path[0]?.state ?? "base_game",
      grid: outcome.grid,
      multiplier: 1,
      win: outcome.winnings,
      removed: [],
      dropped: [],
    });
  }
  return rounds;
}

/** True when a replayed outcome contains a real tumble (≥2 rounds). */
export function isCascade(outcome: ReplayedOutcome): boolean {
  let evaluates = 0;
  for (const step of outcome.state_path) {
    if (step.action_kind === "evaluate") evaluates++;
    if (evaluates >= 2) return true;
  }
  return false;
}

/**
 * Pick the most illustrative cascading spin from a sample: the one with
 * the most cascade rounds (the deepest, most legible tumble). Returns
 * `null` when no sampled spin cascades — the panel stays hidden for
 * non-cascading games rather than rendering a redundant single round
 * (the plain spin replay already covers those).
 */
export function pickCascadeOutcome(
  outcomes: ReplayedOutcome[],
): { outcome: ReplayedOutcome; index: number } | null {
  let best: { outcome: ReplayedOutcome; index: number; rounds: number } | null =
    null;
  for (let i = 0; i < outcomes.length; i++) {
    const o = outcomes[i];
    if (o === undefined || !isCascade(o)) continue;
    const rounds = extractCascadeRounds(o).length;
    if (best === null || rounds > best.rounds) {
      best = { outcome: o, index: i, rounds };
    }
  }
  return best === null ? null : { outcome: best.outcome, index: best.index };
}

// ── JSON-shape coercion helpers ────────────────────────────────────

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asGrid(value: unknown): string[][] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const grid: string[][] = [];
  for (const column of value) {
    if (!Array.isArray(column)) return null;
    grid.push(column.map(String));
  }
  return grid;
}

function asPositions(value: unknown): Array<[number, number]> {
  if (!Array.isArray(value)) return [];
  const positions: Array<[number, number]> = [];
  for (const entry of value) {
    if (
      Array.isArray(entry) &&
      entry.length === 2 &&
      typeof entry[0] === "number" &&
      typeof entry[1] === "number"
    ) {
      positions.push([entry[0], entry[1]]);
    }
  }
  return positions;
}

/** Positions where two reel-major grids differ (cell-by-cell). */
function diffCells(a: string[][], b: string[][]): Array<[number, number]> {
  const diff: Array<[number, number]> = [];
  for (let reel = 0; reel < b.length; reel++) {
    const colB = b[reel] ?? [];
    const colA = a[reel] ?? [];
    for (let row = 0; row < colB.length; row++) {
      if (colB[row] !== colA[row]) diff.push([reel, row]);
    }
  }
  return diff;
}
